/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { isChannelTypeB2B } from '@msdyn365-commerce/core';
import { AsyncResultStatus, Cart, CartLineValidationResults, format } from '@msdyn365-commerce/retail-proxy';
import {
    updateLineDeliverySpecificationsAsync,
    validateForCheckoutAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import {
    Address,
    CartLine,
    CustomerBalances,
    DeliveryPreferenceType,
    LineDeliverySpecification,
    SimpleProduct,
    TokenizedPaymentCard
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { IModuleStateManager, IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import {
    ArrayExtensions,
    getAvailabilitiesForCartLineItems,
    IProductInventoryInformation,
    ProductAvailabilitiesForCartLineItems,
    validateCartLinesInventory
} from '@msdyn365-commerce-modules/retail-actions';
import {
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    IModuleProps,
    INodeProps,
    ITelemetryContent,
    TelemetryConstant,
    Waiting,
    Modal,
    ModalBody,
    ModalHeader
} from '@msdyn365-commerce-modules/utilities';
import { ErrorCode, ErrorLocation, ICartActionResult, ICheckoutActionResult } from '@msdyn365-commerce/global-state';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { action, computed, get, IReactionDisposer, reaction, when } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { IDlvryCheckoutV2Data } from './dlvry-checkout-v2.data';
import { ICheckoutHeadingData, IDlvryCheckoutV2Props } from './dlvry-checkout-v2.props.autogenerated';
import AlertComponent from './components/alert';
import { getInvoicePaymentSummary, IInvoicePaymentSummary } from './components/get-invoice-payment-summary';
import { ILineItems } from './components/get-line-items';
import { getOrderSummary, IOrderSummary } from './components/get-order-summary';
import GuidedFormComponent from './components/guided-form';
import PlaceOrderButtonComponent from './components/place-order-button';
import placeOrder from './methods/place-order';
import { focusOnCheckoutError } from '@msdyn365-commerce-modules/checkout';
import { AsyncResultStatusCode, IExpressPaymentDetails } from '@msdyn365-commerce-modules/checkout';
import { getByIdsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { updateCartAsync } from '../../actions/update-cart-as-post';
import * as GTM from '../../shared-utils/google-tag-manager/gtm';

export * from './components/get-line-items';
export * from './components/get-order-summary';

/**
 * Device type.
 */
type DeviceType = 'Mobile';

/**
 * The Checkout state.
 */
export interface ICheckoutState {
    errorMessage: string;
    errorDetails: string;
    errorCorrelationId: string;
    errorTimeout: boolean;
    isValidationPassed: boolean;
    isPlaceOrderLoading?: boolean;
    isPlaceOrderClicked?: boolean;
    hasCalculatedTotal?: boolean;
    isCalculatingTotal?: boolean;
    productNames: { [key: number]: string };
    showSplitUpDeliveryModal: boolean;
    retrievingProductNames: boolean;
    hideReminderMessage?: boolean;
}

/**
 * Known action error failure states
 */
export const enum ActionErrorStatus {
    Timeout = 'Timeout',
    HookTimeout = 'Hook Timeout',
    ServiceUnavailable = 'Service Unavailable'
}

/**
 * The Checkout view props.
 * @extends IDlvryCheckoutV2Props<IDlvryCheckoutV2Data>
 */
export interface ICheckoutViewProps extends IDlvryCheckoutV2Props<IDlvryCheckoutV2Data> {
    className: string;
    canShow?: boolean;
    isCalculatingTotal?: boolean;
    isEmptyCart?: boolean;
    isConsentGiven?: boolean;
    hasError?: boolean;
    isPaymentVerificationRedirection?: boolean;
    shouldEnableSinglePaymentAuthorizationCheckout?: boolean;
    cartStatus?: string;
    isMobile?: boolean;
    isEditorialMode?: boolean;
    backToShoppingUrl?: string;
    editCartLink?: string;
    hasSalesOrder: boolean;
    hasInvoiceLine?: boolean;

    moduleState?: IModuleStateManager;
    telemetryContent?: ITelemetryContent;
    checkoutGuidedFormHasError?: boolean;

    checkoutProps: IModuleProps;
    headerProps: INodeProps;
    bodyProps: INodeProps;
    mainProps: INodeProps;
    mainControlProps: INodeProps;
    sideProps: INodeProps;
    sideControlFirstProps: INodeProps;
    sideControlSecondProps: INodeProps;
    sideControlThirdProps: INodeProps;
    termsAndConditionsProps: INodeProps;

    guidedForm?: React.ReactNode;
    title?: React.ReactNode;
    loading?: React.ReactNode;
    alert?: React.ReactNode;
    orderSummary?: IOrderSummary;
    invoicePaymentSummary?: IInvoicePaymentSummary;
    lineItems?: ILineItems;
    calculateTotalButton?: React.ReactNode;
    placeOrderButton?: React.ReactNode;
    termsAndConditions?: React.ReactNode;
    keepShoppingButton?: React.ReactNode;
    orderConfirmation?: React.ReactNode;

    checkoutExpressPaymentContainer?: React.ReactNode;
    checkoutErrorRef?: React.RefObject<HTMLElement>;

    splitDeliveryModal?: ICheckoutSplitDeliveryModalProps;
    reminderMessage?: React.ReactNode;
}

/**
 * The checkout module props interface.
 */
export interface ICheckoutModuleProps extends IDlvryCheckoutV2Props<IDlvryCheckoutV2Data>, IModuleStateProps {}

export interface ICheckoutSplitDeliveryModalProps {
    Container: INodeProps | null;
    header: React.ReactNode;
    message: React.ReactNode;
    button: React.ReactNode;
    modal: React.ReactNode;
}

const expressPaymentSectionClassName = 'msc-express-payment-container';

/**
 *
 * CheckoutContainer component.
 * @extends {React.Component<ICheckoutModuleProps>}
 */
// @ts-expect-error
@withModuleState
@observer
class Checkout extends React.PureComponent<ICheckoutModuleProps> {
    private calculateTotalWhenDisposer: IReactionDisposer | undefined;

    @computed get cartStatus(): Msdyn365.AsyncResultStatus {
        // Since data.checkout is supposed to be deferred but it will report it's status a 'SUCCESS' immediately,
        // we need to fake the status a bit.
        return this.props.data.checkout?.status === 'SUCCESS'
            ? this.props.data.checkout.result?.timedOut
                ? 'FAILED'
                : this.props.data.checkout.result?.status === 'UPDATING' ? 'LOADING' : this.props.data.checkout?.status
            : this.props.data.checkout?.status;
    }

    @computed get isEmptyCart(): boolean {
        if (this.isEditorialMode) {
            // Editorial mode: Ignore empty cart
            return false;
        }
        const cart = this.props.data.checkout?.result?.checkoutCart?.cart;
        return this.cartStatus === 'SUCCESS' && !!cart && !isEmpty(cart) && (!cart.CartLines || cart.CartLines.length === 0);
    }

    @computed get hasError(): boolean {
        const cart = this.props.data.checkout?.result?.checkoutCart?.cart;
        return this.cartStatus === 'FAILED' || (this.cartStatus === 'SUCCESS' && (!cart || isEmpty(cart)));
    }

    @computed get isEditorialMode(): boolean {
        return this.props.context.request.params.isEditor;
    }

    @computed get canPlaceOrder(): boolean {
        const isTermsAndConditionAccepted = this.props.data.checkout.result && this.props.data.checkout.result?.isTermsAndConditionAccepted;

        const shouldEnableSinglePaymentAuthorizationCheckout =
            this.props.data.checkout.result?.shouldEnableSinglePaymentAuthorizationCheckout ?? false;

        const disableForOBO = Msdyn365.isOboRequest(this.props.context.request) && !this.isPaidOffByCustomerAccount;

        // If isTermsAndConditionAccepted is undefined means TermsAndCondition module is not added to page and we should able to place order.
        return (
            this.props.data.checkout.result?.checkoutCart.status === 'READY' &&
            this.props.moduleState.isReady &&
            (isTermsAndConditionAccepted === undefined || isTermsAndConditionAccepted || shouldEnableSinglePaymentAuthorizationCheckout) &&
            (this.state.errorMessage === '' ||
                shouldEnableSinglePaymentAuthorizationCheckout ||
                (this.props.data.checkout.result?.isPaymentVerificationRedirection ?? false)) &&
            !(shouldEnableSinglePaymentAuthorizationCheckout && disableForOBO)
        );
    }

    @computed get isPaidOffByCustomerAccount(): boolean {
        const amountDue = this.props.data.checkout.result?.checkoutCart.cart.TotalAmount ?? 0;
        const customerAccountAmount = this.props.data.checkout.result?.customerAccountAmount ?? 0;

        return customerAccountAmount === amountDue;
    }

    @computed get isCartReady(): boolean {
        return (
            !this.props.data.checkout.result?.deferred &&
            !!this.props.data.checkout.result?.checkoutCart?.cart &&
            this.props.data.customerInformation.status !== 'LOADING' &&
            this.props.data.featureState.status !== 'LOADING'
        );
    }

    @computed get getLoyaltyAmount(): number {
        const checkoutState = this.props.data.checkout.result;
        if (!checkoutState || !checkoutState.loyaltyAmount) {
            return 0;
        }
        return checkoutState.loyaltyAmount;
    }

    @computed get maxPaymentAmount(): number {
        const cart = this.props.data.checkout.result ? this.props.data.checkout.result.checkoutCart.cart : undefined;
        if (!cart) {
            return 0;
        }

        // Use customer account after loyalty.
        const amountDue = Math.max(0, (cart.TotalAmount || 0) - this.getLoyaltyAmount);

        // If the user has a mandatory credit limit, then the max amount must not be more than that
        if (this.props.data.customerInformation?.result?.MandatoryCreditLimit) {
            return Math.min(this.getAvailableCredit(this.props.data.creditBalances?.result), amountDue);
        }
        return amountDue;
    }

    public get expressPaymentDetailsFromCartPage(): IExpressPaymentDetails | null {
        const properties =
            this.props.data.checkout?.result?.checkoutCart?.cart?.ExtensionProperties?.find(
                property => property.Key === 'expressPaymentDetails'
            )?.Value?.StringValue ?? '';
        return properties ? (JSON.parse(properties) as IExpressPaymentDetails) : null;
    }

    public state: ICheckoutState = {
        errorMessage: '',
        errorDetails: '',
        errorCorrelationId: '',
        errorTimeout: false,
        isValidationPassed: false,
        isPlaceOrderLoading: false,
        isPlaceOrderClicked: false,
        hasCalculatedTotal: false,
        isCalculatingTotal: false,
        productNames: {},
        showSplitUpDeliveryModal: false,
        retrievingProductNames: false
    };

    private readonly telemetryContent: ITelemetryContent = getTelemetryObject(
        this.props.context.request.telemetryPageName!,
        this.props.friendlyName,
        this.props.telemetry
    );

    private readonly checkoutErrorRef: React.RefObject<HTMLElement> = React.createRef();

    private readonly showCalculateButton: boolean = false;

    public componentDidMount(): void {
        const {
            resources: { genericErrorMessage }
        } = this.props;

        const initPromise = this.props.data.checkout.result?.initialize(false)
            .catch((error) => {
                // There should be other handlers for other types of errors, this only needs to handle timeout
                if (error.name === 'ActionError' && error.status === 'Timeout') {
                    this.props.data.checkout.result?.setTimedOut(true);
                    // Show timeout error message
                    this.setState({ errorTimeout: true });
                }
            });

        void Promise.all([
            this.props.data.checkout,
            this.props.data.products,
            initPromise
        ]).then(async ([checkout, products]) => {
            await GTM.beginCheckoutEventAsync(checkout.checkoutCart.cart, products);
        });

        when(
            () => this.asyncResultStatus !== AsyncResultStatusCode.LOADING,
            async () => {
                if (this.expressPaymentDetailsFromCartPage) {
                    const {
                        email,
                        tenderLine,
                        tokenizedPaymentCard,
                        paymentTenderType: paymentTenderTypeFromCartPage,
                        isExpressCheckoutAppliedInCartPage,
                        isExpressAddressAppliedInCartPage,
                        cardPaymentAcceptResult,
                        address
                    } = this.expressPaymentDetailsFromCartPage;

                    this.props.data.checkout.result?.updateExpressCheckoutDetails({
                        newEmailAddressFromExpressCheckout: email ?? '',
                        newTenderLine: tenderLine,
                        newTokenizedPaymentCard: tokenizedPaymentCard,
                        newPaymentTenderType: paymentTenderTypeFromCartPage,
                        newIsExpressCheckoutApplied: isExpressCheckoutAppliedInCartPage,
                        newIsExpressAddressApplied: isExpressAddressAppliedInCartPage,
                        newCardPaymentAcceptResult: cardPaymentAcceptResult
                    });

                    if (isExpressAddressAppliedInCartPage && address) {
                        this.props.data.checkout.result?.updateBillingAddressFromExpressCheckout({
                            newBillingAddressFromExpressCheckout: address
                        });
                        this.props.data.checkout.result?.updateShippingAddressFromExpressCheckout({
                            newShippingAddressFromExpressCheckout: address
                        });
                    } else {
                        void this._saveShippingAndBillingAddress(tokenizedPaymentCard);
                    }

                    this.props.data.checkout.result?.checkoutCart.updateExtensionProperties({
                        newExtensionProperties: [
                            {
                                Key: 'expressPaymentDetails',
                                Value: { StringValue: '' }
                            }
                        ]
                    });
                }
            }
        );

        when(
            () => this.hasError,
            () => {
                this.setState({
                    errorMessage: genericErrorMessage,
                    errorDetails: '',
                    errorCorrelationId: ''
                });
                this.props.context.telemetry.error('Failed to get cart');
            }
        );

        when(
            () => {
                return this.isEmptyCart;
            },
            () => {
                this.redirectToCartPage('Cart is empty, but no editCartLink found');
            }
        );

        when(
            () => this.isCartReady,
            async () => {
                await this.validateForCheckout();

                /* if (this.props.config.autoApplyMaxAvailableCredit) {
                    await this.addPaymentMax();
                } */
            }
        );

        reaction(
            () => this.props.data.checkoutAppliedState.result,
            () => {
                this.setState({ hasCalculatedTotal: false });
            }
        );

        reaction(
            () => this.props.data.checkoutAppliedState.result,
            () => {
                if (this.state.errorMessage === this.props.resources.invalidRequestedDeliveryDate) {
                    this.props.moduleState.setHasError(false);
                    this.props.moduleState.onUpdating();
                    this.setState({ errorMessage: '', errorDetails: '', errorCorrelationId: '' });
                }
            }
        );

        reaction(
            () => this.props.moduleState.isReady,
            () => {
                this.onContainerReady();
            }
        );

        // When the cart.TotalAmount gets updated (like if selecting the delivery option adds a charge) we need to update the amount based on the new total amount.
        reaction(
            () =>
                this.props.data.checkout.result &&
                this.props.data.checkout.result.checkoutCart.cart &&
                this.props.data.checkout.result.checkoutCart.cart.TotalAmount,
            async totalAmount => {
                if (this.props.config.autoApplyMaxAvailableCredit && this.state.hasCalculatedTotal) {
                    await this.addPaymentMax();
                }
            }
        );

        // Reaction to call place order when isPaymentSectionContainerReady is true for new checkout flow.
        reaction(
            () => this.props.data.checkout.result?.isPaymentSectionContainerReady,
            async isReady => {
                if (
                    this.props.data.checkout.result?.shouldEnableSinglePaymentAuthorizationCheckout &&
                    isReady &&
                    !this.props.data.checkout.result?.redirectAdditionalContext &&
                    (!this.props.data.checkout.result?.isExpressCheckoutApplied ||
                        this.props.data.checkout.result?.isPaymentVerificationRedirection)
                ) {
                    if (!this.state.isPlaceOrderLoading) {
                        this.setState({ isPlaceOrderLoading: true });
                    }

                    this.props.telemetry.information(
                        'Call onPlaceOrder as reaction when isPaymentSectionContainerReady is updated for single auth payment.'
                    );

                    await this.onPlaceOrder();

                    // If 3D authentication redirection, don't update isPlaceOrderLoading
                    if (!this.props.data.checkout.result.isPaymentVerificationRequired) {
                        this.setState({ isPlaceOrderLoading: false });
                    }
                }
            }
        );

        // Reaction to enable place order button when isPaymentSectionContainerHasError is true for new checkout flow.
        reaction(
            () => this.props.data.checkout.result?.isPaymentSectionContainerHasError,
            hasError => {
                if (this.props.data.checkout.result?.shouldEnableSinglePaymentAuthorizationCheckout && hasError) {
                    this.setState({ isPlaceOrderLoading: false });

                    this.props.telemetry.information(
                        'Enable place order button as reaction when isPaymentSectionContainerHasError is updated to true for single auth payment.'
                    );
                } else {
                    this.props.telemetry.information(
                        'No reaction when isPaymentSectionContainerHasError is updated to false or not single auth payment.'
                    );
                }
            }
        );

        reaction(
            () => this.props.data.checkout.result?.isBillingAddressHasError,
            hasError => {
                if (this.props.data.checkout.result?.shouldEnableSinglePaymentAuthorizationCheckout && hasError) {
                    this.setState({ isPlaceOrderLoading: false });

                    this.props.data.checkout.result?.updateIsBillingAddressHasError({ newIsBillingAddressHasError: false });

                    this.props.telemetry.information(
                        'Enable place order button as reaction when isBillingAddressHasError is updated to true for single auth payment.'
                    );
                } else {
                    this.props.telemetry.information(
                        'No reaction when isBillingAddressHasError is updated to false or not single auth payment.'
                    );
                }
            }
        );

        // Reaction to enable place order button when redirection is true for new checkout flow.
        reaction(
            () => this.canPlaceOrder,
            async () => {
                if (this.props.data.checkout.result?.shouldEnableSinglePaymentAuthorizationCheckout && this.canPlaceOrder) {
                    if (this.props.data.checkout.result?.isPaymentVerificationRedirection) {
                        await this.props.data.checkout.result.updateIsPlaceOrderEnabledInRedirection({
                            newIsPlaceOrderEnabledInRedirection: true
                        });

                        this.props.telemetry.information(
                            'Set isPaymentVerificationRedirection to true as reaction when canPlaceOrder is updated to true for single auth payment.'
                        );
                    } else {
                        this.props.telemetry.information(
                            'No reaction when canPlaceOrder is updated to true but isPaymentVerificationRedirection is false for single auth payment.'
                        );
                        this.props.telemetry.information(
                            `isOBORequest is ${Msdyn365.isOboRequest(this.props.context.request)} and canPlaceOrder is ${
                                this.canPlaceOrder
                            }.`
                        );
                    }
                } else {
                    this.props.telemetry.information('No reaction when canPlaceOrder is updated to false or not single auth payment.');
                }
            }
        );

        // Reaction to update the error message when there is a checkout error.
        reaction(
            () => this.props.data.checkout.result?.checkoutError,
            checkoutError => {
                const checkoutErrorLocation: boolean = checkoutError?.errorLocation === ErrorLocation.Checkout;
                // If there is an Error in CheckoutPaymentInstrument module, checkout error location will be updated.
                const checkoutPaymentErrorLocation: boolean =
                    checkoutError?.errorCode === ErrorCode.Microsoft_Dynamics_Commerce_Runtime_UnableToAuthorizePayment
                        ? checkoutError.errorLocation === ErrorLocation.CheckoutPaymentInstrument
                        : checkoutErrorLocation;
                if (
                    this.props.data.checkout.result?.shouldEnableCheckoutErrorDisplayMessaging &&
                    checkoutError &&
                    checkoutError.errorMessage &&
                    (checkoutErrorLocation ? checkoutErrorLocation : checkoutPaymentErrorLocation)
                ) {
                    this._setErrorMessage(checkoutError.errorMessage);
                }
            }
        );
    }

    public componentDidUpdate(): void {
        if (
            this.props.data.checkout.result?.shouldEnableCheckoutErrorDisplayMessaging &&
            this.props.data.checkout.result?.shouldFocusOnCheckoutError &&
            this.state.errorMessage
        ) {
            void focusOnCheckoutError(this.checkoutErrorRef, this.props.context.actionContext);
        }
    }

    // eslint-disable-next-line complexity -- ignore the complexity.
    public render(): JSX.Element {
        const {
            moduleState,
            config: { className, checkoutHeading, disableGuidedCheckoutFlow },
            resources
        } = this.props;
        const {
            errorMessage,
            errorDetails,
            errorCorrelationId
        } = this.state;
        const { calculateTotalText, backToShopping, placeOrderText, confirmPaymentText, genericErrorMessage } = resources;
        const checkoutClass = classnames('ms-checkout', className);
        const allCheckoutInformation = this.getSlotItems('checkoutInformation');

        // @ts-expect-error - check the key of slot
        const checkoutInformation = allCheckoutInformation?.filter(info => !info?.key.includes('express-payment'));

        // @ts-expect-error - check the key of slot
        const checkoutExpressPaymentContainer = allCheckoutInformation?.find(info => info?.key.includes('express-payment'));

        const isConsentGiven =
            this.props.context.request &&
            this.props.context.request.cookies &&
            this.props.context.request.cookies.isConsentGiven &&
            this.props.context.request.cookies.isConsentGiven();

        const isMobile =
            this.props.context.request &&
            this.props.context.request.device &&
            (this.props.context.request.device.Type as DeviceType) === 'Mobile';

        const backToShoppingUrl = Msdyn365.getUrlSync('home', this.props.context.actionContext) || '';
        const editCartLink = Msdyn365.getUrlSync('cart', this.props.context.actionContext) || '';

        const termsAndConditions =
            this.props.slots.termsAndConditions && this.props.slots.termsAndConditions.length > 0 && this.props.slots.termsAndConditions[0];

        const payLoad = getPayloadObject('click', this.telemetryContent, TelemetryConstant.BackToShopping);
        const backToShoppingAttributes = getTelemetryAttributes(this.telemetryContent, payLoad);

        const hasInvoiceLine = this.props.data.checkout && this.props.data.checkout.result?.checkoutCart.hasInvoiceLine;
        const checkoutBtnText = hasInvoiceLine ? confirmPaymentText : placeOrderText;

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check for null/undefined.
        const shouldEnableSinglePaymentAuthorizationCheckout =
            this.props.data.checkout?.result?.shouldEnableSinglePaymentAuthorizationCheckout ?? false;

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check null/undefined.
        const isExpressCheckoutApplied = this.props.data.checkout?.result?.isExpressCheckoutApplied ?? false;

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check null/undefined.
        const hasShippingAddress = this.props.data.checkout?.result?.hasShippingAddress;

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check null/undefined.
        const hasCartDeliveryMode = this.props.data.checkout?.result?.hasCartDeliveryMode;

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check null/undefined.
        const hasGuestCheckoutEmail = this.props.data.checkout?.result?.hasGuestCheckoutEmail;

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check null/undefined.
        const isTermsAndConditionAccepted = this.props.data.checkout?.result?.isTermsAndConditionAccepted;

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check null/undefined.
        const isPaymentVerificationRedirection = this.props.data.checkout?.result?.isPaymentVerificationRedirection ?? false;

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check null/undefined.
        const shouldEnableCheckoutErrorDisplayMessaging =
            this.props.data.checkout?.result?.shouldEnableCheckoutErrorDisplayMessaging ?? false;

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Explicitly check null/undefined.
        const shouldFocusOnCheckoutError = this.props.data.checkout?.result?.shouldFocusOnCheckoutError;

        let hasExpressPaymentContainer = false;

        if (
            typeof window !== 'undefined' &&
            (document?.getElementsByClassName(expressPaymentSectionClassName) as HTMLCollectionOf<HTMLElement>)[0]?.style.display === ''
        ) {
            hasExpressPaymentContainer = true;
        }

        let viewProps: ICheckoutViewProps = {
            ...this.props,
            className: checkoutClass,
            checkoutErrorRef: this.checkoutErrorRef,
            isEmptyCart: this.isEmptyCart,
            isConsentGiven,
            hasError: this.hasError,
            isPaymentVerificationRedirection: this.props.data.checkout?.result?.isPaymentVerificationRedirection,
            shouldEnableSinglePaymentAuthorizationCheckout,
            hasSalesOrder:
                this.props.data.checkout &&
                this.props.data.checkout.result !== undefined &&
                this.props.data.checkout.result.salesOrder !== undefined,
            hasInvoiceLine,
            cartStatus: this.cartStatus,
            isMobile,
            backToShoppingUrl,
            editCartLink,
            isEditorialMode: this.isEditorialMode,
            orderConfirmation:
                this.props.slots.orderConfirmation && this.props.slots.orderConfirmation.length > 0
                    ? this.props.slots.orderConfirmation[0]
                    : undefined,
            checkoutProps: { moduleProps: this.props, className: checkoutClass },
            headerProps: { className: 'ms-checkout__head' },
            bodyProps: { className: 'ms-checkout__body' },
            mainProps: { className: 'ms-checkout__main' },
            mainControlProps: { className: 'ms-checkout__main-control' },
            sideProps: { className: hasExpressPaymentContainer ? 'ms-checkout__has-express-side' : 'ms-checkout__side' },
            sideControlFirstProps: { className: 'ms-checkout__side-control-first' },
            sideControlSecondProps: { className: 'ms-checkout__side-control-second' },
            sideControlThirdProps: { className: 'ms-checkout__side-control-third' },
            termsAndConditionsProps: { className: 'ms-checkout__terms-and-conditions' },
            title: checkoutHeading && this.renderMsdyn365Text(checkoutHeading),
            checkoutExpressPaymentContainer,
            splitDeliveryModal: this.getSplitUpDeliveryMessage(),
            reminderMessage: this.getReminderMessage()
        };

        if (this.isLoading()) {
            viewProps = {
                ...viewProps,
                loading: (
                    <div className='msc-waiting-wrapper'>
                        <Waiting className='msc-waiting-circular msc-waiting-lg' />
                        <div className='msc-waiting-text'>{this.props.resources.checkoutLoadingText}</div>
                    </div>
                )
            };
        } else if (this.state.errorTimeout) {
            viewProps = {
                ...viewProps,
                alert: <AlertComponent {...{ message: this.props.resources.checkoutCopyTimeoutErrorMessage }} />
            };
        } else if (this.hasError) {
            viewProps = {
                ...viewProps,
                alert: <AlertComponent {...{ message: genericErrorMessage }} />
            };
        } else {
            viewProps = {
                ...viewProps,
                alert: errorMessage &&
                    <AlertComponent
                        message={errorMessage}
                        details={errorDetails}
                        correlationId={errorCorrelationId}
                    />,
                canShow: true,
                isCalculatingTotal: this.state.isCalculatingTotal,
                guidedForm: checkoutInformation ? (
                    <GuidedFormComponent
                        {...{
                            items: checkoutInformation,
                            cartReady: this.props.data.checkout.result?.checkoutCart.status === 'READY',
                            moduleState,
                            disableGuidedCheckoutFlow,
                            resource: resources,
                            requestContext: this.props.context,
                            isMobile,
                            isEditor: this.isEditorialMode,
                            telemetryContent: this.telemetryContent,
                            isPaymentVerificationRedirection: this.props.data.checkout?.result?.isPaymentVerificationRedirection,
                            shouldEnableSinglePaymentAuthorizationCheckout,
                            hasError: !!errorMessage,
                            hasShippingAddress,
                            hasCartDeliveryMode,
                            hasGuestCheckoutEmail,
                            isTermsAndConditionAccepted,
                            shouldEnableCheckoutErrorDisplayMessaging,
                            shouldFocusOnCheckoutError,
                            onSubmit: async (step, key, title) => {
                                const checkout = await this.props.data.checkoutAppliedState;
                                const applied = checkout.applied;

                                if (this.calculateTotalWhenDisposer) {
                                    this.calculateTotalWhenDisposer();
                                    this.calculateTotalWhenDisposer = undefined;
                                }

                                // Wait for state to update, will take a little bit
                                this.calculateTotalWhenDisposer = when(
                                    // Calculate total if all information is applied
                                    () => applied.customerInformation && applied.deliveryOptions && applied.shippingInformation /* && this.props.moduleState.isReady */,
                                    async () => {
                                        // Check if calculate should be available, for example if a calculation is already in progress etc
                                        if (!this.state.isCalculatingTotal && !this.state.hasCalculatedTotal/*  && this.canPlaceOrder */) {
                                            await this.calculateTotal();
                                        }
                                    },
                                    {
                                        timeout: 2000,
                                        onError: () => {
                                            this.calculateTotalWhenDisposer = undefined;
                                            console.log('Waiting for all information timed out');
                                        }
                                    }
                                );
                            }
                        }}
                    />
                ) : (
                    undefined
                ),
                orderSummary: getOrderSummary(this.props, this.state),
                invoicePaymentSummary: getInvoicePaymentSummary(this.props),
                lineItems: this.renderEmptyLineItems(),
                calculateTotalButton: this.showCalculateButton && (
                    <Button
                        className={`ms-checkout__btn-calculate-total msc-btn${this.state.isCalculatingTotal ? ' is-busy' : ''}`}
                        title={calculateTotalText}
                        color='primary'
                        disabled={this.state.isCalculatingTotal || this.state.hasCalculatedTotal || !this.canPlaceOrder}
                        onClick={this.calculateTotal}
                    >
                        {calculateTotalText}
                    </Button>
                ),
                placeOrderButton: (
                    <PlaceOrderButtonComponent
                        {...{
                            checkoutBtnText,
                            placeOrder:
                                !isExpressCheckoutApplied && shouldEnableSinglePaymentAuthorizationCheckout
                                    ? this.triggerPaymentWithPlaceOrder
                                    : this.onPlaceOrder,

                            canPlaceOrder: !!this.state.hasCalculatedTotal && this.canPlaceOrder,
                            telemetryContent: this.telemetryContent,
                            shouldEnableSinglePaymentAuthorizationCheckout: this.props.data.checkout.result
                                ?.shouldEnableSinglePaymentAuthorizationCheckout,
                            isPlaceOrderLoading: this.state.isCalculatingTotal || this.state.isPlaceOrderLoading,
                            isExpressCheckoutApplied,
                            isPaymentVerificationRedirection
                        }}
                    />
                ),
                termsAndConditions,
                keepShoppingButton: backToShoppingUrl && (
                    <Button
                        className='ms-checkout__btn-keep-shopping msc-btn'
                        title={backToShopping}
                        color='secondary'
                        href={backToShoppingUrl}
                        {...backToShoppingAttributes}
                        disabled={this.state.isPlaceOrderClicked}
                    >
                        {backToShopping}
                    </Button>
                ),
                moduleState,
                telemetryContent: this.telemetryContent,
                checkoutGuidedFormHasError: !!errorMessage
            };
        }

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    @computed public get asyncResultStatus(): AsyncResultStatus | undefined {
        const isLoading = Object.values(this.props.data).some(data => get(data, 'status') === AsyncResultStatusCode.LOADING);
        if (isLoading) {
            return AsyncResultStatusCode.LOADING;
        }

        const isSuccess = Object.values(this.props.data).every(data => get(data, 'status') === AsyncResultStatusCode.SUCCESS);
        if (isSuccess) {
            return AsyncResultStatusCode.SUCCESS;
        }

        const isFailed = Object.values(this.props.data).some(data => get(data, 'status') === AsyncResultStatusCode.FAILED);
        if (isFailed) {
            return AsyncResultStatusCode.FAILED;
        }

        return undefined;
    }

    /**
     * Renders heading.
     * @param checkoutHeading - The checkout heading.
     * @returns React node.
     */
    public renderMsdyn365Text(checkoutHeading: ICheckoutHeadingData): React.ReactNode {
        return (
            <Msdyn365.Text
                {...checkoutHeading}
                tag={checkoutHeading.tag || 'h2'}
                className='ms-checkout__title'
                text={checkoutHeading.text}
                editProps={{ onEdit: this.handleCheckoutHeadingChange, requestContext: this.props.context.request }}
            />
        );
    }

    private readonly renderEmptyLineItems = (): ILineItems => {
        const {
            context,
            config: { lineItemsHeading },
            resources: { editCartText }
        } = this.props;
        const payLoad = getPayloadObject('click', this.telemetryContent, TelemetryConstant.EditCart);
        const attribute = getTelemetryAttributes(this.telemetryContent, payLoad);

        return {
            editLink: (
                <Button
                    className='ms-checkout__line-items-edit-cart-link'
                    title={editCartText}
                    color='link'
                    href={Msdyn365.getUrlSync('cart', context.actionContext) || ''}
                    {...attribute}
                >
                    {editCartText}
                </Button>
            ),
            Header: { className: 'ms-checkout__line-items-header empty' },
            heading: lineItemsHeading && lineItemsHeading.text && (
                <Msdyn365.Text
                    className='ms-checkout__line-items-heading'
                    {...lineItemsHeading}
                    tag={lineItemsHeading.tag || 'h2'}
                    text={lineItemsHeading.text}
                    editProps={{ onEdit: (event: Msdyn365.ContentEditableEvent) => {
                        if (lineItemsHeading) {
                            lineItemsHeading.text = event.target.value;
                        }
                    }, requestContext: context.request }}
                />),
            LineItems: { className: `ms-checkout__line-items multiple-pickup-enabled` }
        };
    };

    /**
     * Event handler for heading change event.
     * @param event - The content change event in heading element.
     * @returns The new heading value.
     */
    public handleCheckoutHeadingChange = (event: Msdyn365.ContentEditableEvent) =>
        (this.props.config.checkoutHeading!.text = event.target.value);

    /**
     * Checks if cart is loading.
     * @returns True if is loading, false otherwise.
     */
    private readonly isLoading = (): boolean => {
        return !this.cartStatus || this.cartStatus === 'LOADING' || this.isEmptyCart || !this.state.isValidationPassed;
    };

    private readonly isRequestedDeliveryDateInPast = (): boolean => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const requestedDeliveryDate = this.props.data.checkout.result?.checkoutCart.cart.RequestedDeliveryDate;
        if (!requestedDeliveryDate) {
            return false;
        }
        requestedDeliveryDate.setHours(0, 0, 0, 0);
        return requestedDeliveryDate < today;
    };

    private readonly addPaymentMax = async (): Promise<void> => {
        const checkoutState = this.props.data.checkout.result;

        if (!checkoutState) {
            this.props.context.telemetry.error('checkout state not found');
            return;
        }

        await checkoutState.updateCustomerAccountAmount({ newAmount: this.maxPaymentAmount });
        this.props.context.telemetry.information('customer account payment amount updated');
    };

    private readonly getAvailableCredit = (creditBalances: CustomerBalances | undefined) => {
        let availableCredit = 0;

        if (!creditBalances) {
            return 0;
        }

        if (creditBalances.InvoiceAccountCreditLimit === 0) {
            availableCredit = creditBalances.CreditLimit - creditBalances.Balance - creditBalances.PendingBalance;
        } else {
            availableCredit =
                creditBalances.InvoiceAccountCreditLimit -
                creditBalances.InvoiceAccountBalance -
                creditBalances.InvoiceAccountPendingBalance;
        }

        return availableCredit;
    };

    /**
     * Get slot items.
     * @param key - The slot key.
     * @returns React node or undefined.
     */
    private readonly getSlotItems = (key: string): React.ReactNode[] | undefined => {
        const { slots } = this.props;
        return slots && slots[key] && slots[key].length > 0 ? slots[key] : undefined;
    };

    /**
     * Trigger the payment section container submit when placing order.
     */
    private readonly triggerPaymentWithPlaceOrder = async (): Promise<void> => {
        this.setState({ isPlaceOrderLoading: true });

        await this.props.data.checkout.result?.updateShouldTriggerPaymentWithPlaceOrder({
            newShouldTriggerPaymentWithPlaceOrder: !this.props.data.checkout.result.shouldTriggerPaymentWithPlaceOrder
        });
    };

    /**
     * On place order.
     * @returns Void promise.
     */
    private readonly onPlaceOrder = async (): Promise<void> => {
        const {
            resources,
            context,
            context: {
                actionContext,
                app: {
                    // @ts-ignore
                    config: { enableStockCheck = false }
                }
            },
            slots: { orderConfirmation },
            data: { checkout, products }
        } = this.props;
        const checkoutState = checkout.result;

        if (!checkoutState) {
            // Shouldn't ever be undefined here
            return;
        }

        this.props.telemetry.information('Checkout onPlaceOrder is called.');

        // Check for credit errors
        if (
            Msdyn365.isOboRequest(context.request) &&
            checkoutState?.customerAccountAmount !== checkoutState?.checkoutCart.cart.TotalAmount
        ) {
            this._setErrorMessage(resources.invalidAmountForOBOMessage);
            return;
        } else if (checkoutState.customerAccountAmount > this.maxPaymentAmount) {
            this._setErrorMessage("Too much credit applied.");
            return;
        } else if (this.isRequestedDeliveryDateInPast()) {
            this._setErrorMessage(resources.invalidRequestedDeliveryDate);
            return;
        }

        this.setState({ isPlaceOrderClicked: true });

        /* // If hasInvoiceLine is true, no inventory check
        if (
            !checkout.result?.checkoutCart.hasInvoiceLine &&
            enableStockCheck &&
            (await this.isOverMaxQuantity((await checkout).checkoutCart.cart, await products))
        ) {
            this.setState({
                errorMessage: resources.checkoutOutOfStockErrorMessage
            });
            context.telemetry.error(resources.checkoutOutOfStockErrorMessage);
        } else {
            // Below code block was here
        } */

        const hasOrderConfirmation = orderConfirmation && orderConfirmation.length > 0;
        const updatedCartVersion = await this.updateCartLineEmailAddress(checkout.result?.guestCheckoutEmail || '');

        await placeOrder(
            actionContext,
            checkout.result,
            products.result,
            !hasOrderConfirmation,
            updatedCartVersion,
            checkout.result?.isPaymentVerificationRedirection
        ).catch(error => {
            this.handleCheckoutError(error);
        });

        await checkout.result?.updateIsPaymentSectionContainerReady({ newIsPaymentSectionContainerReady: false });
    };

    /**
     * Update receipt email address for electronic cart line.
     * @param newGuestCheckoutEmail - The guest checkout email.
     * @returns The updated cart version.
     */
    private readonly updateCartLineEmailAddress = async (newGuestCheckoutEmail: string): Promise<number | undefined> => {
        const emailDeliveryModeCode = this.props.context.request.channel?.EmailDeliveryModeCode;

        let electronicDeliveryInvoiceAddress: Address;

        const hasShippingAddress = this.props.data.checkout?.result?.hasShippingAddress;

        if (hasShippingAddress && this.props.config.useShippingAddressAsElectronicDeliveryInvoiceAddress) {
            electronicDeliveryInvoiceAddress = this.props.data.checkout?.result?.shippingAddress as Address;
        }

        const emailDeliveryCartLines = this.props.data.checkout.result?.checkoutCart.cart.CartLines?.filter(
            cartLine => cartLine.DeliveryMode && cartLine.DeliveryMode !== '' && cartLine.DeliveryMode === emailDeliveryModeCode
        );

        let updatedCartVersion: number | undefined;
        if (emailDeliveryCartLines && emailDeliveryCartLines.length > 0) {
            const lineDeliverySpecifications: LineDeliverySpecification[] = emailDeliveryCartLines.map(line => {
                return {
                    LineId: line.LineId,
                    DeliverySpecification: {
                        DeliveryModeId: emailDeliveryModeCode,
                        DeliveryPreferenceTypeValue: DeliveryPreferenceType.ElectronicDelivery,
                        ElectronicDeliveryEmailAddress: newGuestCheckoutEmail,
                        DeliveryAddress: hasShippingAddress ? electronicDeliveryInvoiceAddress : undefined
                    }
                };
            });

            await updateLineDeliverySpecificationsAsync(
                { callerContext: this.props.context.actionContext },
                (await this.props.data.checkout).checkoutCart.cart.Id,
                lineDeliverySpecifications
            )
                .then(updatedCart => {
                    updatedCartVersion = updatedCart.Version;
                })
                .catch(error => {
                    this.props.context.actionContext.telemetry.exception(error);
                    throw new Error('[updateLineDeliverySpecificationsAsync] Unable to update electronic delivery email address in cart');
                });
        }

        return updatedCartVersion;
    };

    /**
     * Return true if any product in the cart is over its max quantity.
     * @param cart -- The checkout cart.
     * @param products -- The checkout products.
     * @returns - Is any product in the cart is over max quantity.
     */
    // @ts-ignore
    private readonly isOverMaxQuantity = async (cart: Cart, products: SimpleProduct[]): Promise<boolean> => {
        let isOverMaxQuantity = false;
        const emailDeliveryModeCode = this.props.context.request.channel?.EmailDeliveryModeCode;

        await getAvailabilitiesForCartLineItems(
            new ProductAvailabilitiesForCartLineItems(this.props.context.actionContext.requestContext.apiSettings),
            this.props.context.actionContext
        )
            .then(productInventoryInformation => {
                const productsThatShouldValidateInventory: IProductInventoryInformation[] = [];

                cart.CartLines?.map(cartline => {
                    const cartlineProduct = products.find(product => product.RecordId === cartline.ProductId);

                    // If product is non-stocked or cartline is electronic delivery, then no inventory check
                    const isStockedItem = cartlineProduct?.Behavior?.IsStockedProduct;
                    const isEmailDelivery =
                        cartline.DeliveryMode && cartline.DeliveryMode !== '' ? cartline.DeliveryMode === emailDeliveryModeCode : false;
                    const shouldSkipInventoryCheck = !isStockedItem || isEmailDelivery;
                    if (!shouldSkipInventoryCheck && ArrayExtensions.hasElements(productInventoryInformation)) {
                        const foundProductAvailability = productInventoryInformation.find(productInventory => {
                            return productInventory.ProductAvailableQuantity?.ProductId! === cartline.ProductId;
                        });
                        if (foundProductAvailability) {
                            productsThatShouldValidateInventory.push(foundProductAvailability);
                        }
                    }
                });

                const cartLinesInventoryValidation = validateCartLinesInventory(
                    cart.CartLines ?? [],
                    productsThatShouldValidateInventory,
                    this.props.context.actionContext,
                    products,
                    emailDeliveryModeCode
                );

                // If is invalid then exceeds the available quantity
                isOverMaxQuantity = !cartLinesInventoryValidation.isValid;
                return isOverMaxQuantity;
            })
            .catch(error => {
                this.props.context.actionContext.telemetry.exception(error);
                throw new Error('[getAvailabilitiesForCartLineItems] Unable to get availabilities for products in checkout cart');
            });

        return isOverMaxQuantity;
    };

    /**
     * Gets active child in module state.
     * @returns - The module state manager collection.
     */
    private readonly getActiveChildModuleStates = (): IModuleStateManager[] => {
        const { childIds, getModule } = this.props.moduleState;
        return childIds
            .map(getModule)
            .filter((state): state is IModuleStateManager => !!state && !state.isDisabled && state.hasModuleState);
    };

    /**
     * On container ready.
     */
    private readonly onContainerReady = (): void => {
        this.props.telemetry.information('Checkout onContainerReady is called.');

        this.getActiveChildModuleStates().forEach(state => {
            state.isReady && state.onContainerReady && state.onContainerReady();
        });
    };

    /**
     * Redirects to cart page.
     * @param notFoundEditCartLinkMessage - Not found edit cart link message.
     */
    private readonly redirectToCartPage = (notFoundEditCartLinkMessage: string): void => {
        const editCartLink = Msdyn365.getUrlSync('cart', this.props.context.actionContext) || '';
        if (window && editCartLink) {
            window.location.assign(editCartLink);
        } else {
            this.setState({
                errorMessage: this.props.resources.genericErrorMessage,
                errorDetails: '',
                errorCorrelationId: ''
            });
            this.props.context.telemetry.error(notFoundEditCartLinkMessage);
        }
    };

    /**
     * Validates for checkout.
     * @param retry - Retry flag.
     */
    private readonly validateForCheckout = async (retry: boolean = true): Promise<void> => {
        const notFoundEditCartLinkMessage = "Cart doesn't pass validation, but no editCartLink found";
        const cart = (await this.props.data.checkout).checkoutCart.cart;
        const { Id, Version } = cart;
        if (this._shouldValidateQuantityLimits()) {
            if (Version && Id) {
                validateForCheckoutAsync({ callerContext: this.props.context.actionContext, bypassCache: 'none' }, Id, Version)
                    .then((result: CartLineValidationResults) => {
                        if (result.ValidationFailuresByCartLines?.length !== 0) {
                            this.redirectToCartPage(notFoundEditCartLinkMessage);
                        } else {
                            this.setState({ isValidationPassed: true });
                        }
                    })
                    .catch(error => {
                        this.props.telemetry.warning(error);
                        this.props.telemetry.debug('Unable to validate Cart');
                        if (retry && error.name === 'Microsoft_Dynamics_Commerce_Runtime_InvalidCartVersion') {
                            const cartState = this.props.data.checkout.result?.checkoutCart;
                            cartState
                                ?.refreshCart({})
                                .then(async () => {
                                    await this.validateForCheckout(false);
                                })
                                .catch((refreshError: Error) => {
                                    this.props.telemetry.warning(refreshError.message);
                                    this.props.telemetry.debug('Unable to refresh Cart');
                                });
                        } else {
                            this.redirectToCartPage(notFoundEditCartLinkMessage);
                        }
                    });
            } else {
                this.redirectToCartPage(notFoundEditCartLinkMessage);
            }
        } else {
            this.setState({ isValidationPassed: true });
        }
    };

    @action
    private readonly _setErrorMessage = (errorMessage: string): void => {
        this.props.telemetry.error(errorMessage);
        this.props.moduleState.setHasError(true);
        this.props.moduleState.onUpdating();
        this.setState({
            errorMessage,
            errorDetails: '',
            errorCorrelationId: ''
        });
    };

    private readonly _shouldValidateQuantityLimits = (): boolean => {
        return false;

        // @ts-expect-error
        const featureStatuses = this.props.data.featureState.result;
        const isECommerceQuantityLimitsFeatureEnabledInHq =
            featureStatuses?.find(
                featureState => featureState.Name === 'Dynamics.AX.Application.EcommerceQuantityLimitConfigurationsFeature'
            )?.IsEnabled || false;

        if (isECommerceQuantityLimitsFeatureEnabledInHq) {
            return true;
        }

        const isOrderQuantityLimitsFeatureEnabledInHq =
            featureStatuses?.find(featureState => featureState.Name === 'Dynamics.AX.Application.RetailDefaultOrderQuantityLimitsFeature')
                ?.IsEnabled || false;

        if (!isOrderQuantityLimitsFeatureEnabledInHq) {
            return false;
        }

        const useSiteBuilderSettings = featureStatuses?.find(
            featureState => featureState.Name === 'RevertToSiteBuilderOrderQuantityLimitsSettings'
        )?.IsEnabled;

        if (useSiteBuilderSettings) {
            const defaultOrderQuantityLimitsFeatureConfig = this.props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits;
            const customerInfo = this.props.data.customerInformation.result;
            const isB2BSite = isChannelTypeB2B(this.props.context.actionContext.requestContext);

            const isOrderQuantityLimitsFeatureEnabledInSiteSettings =
                defaultOrderQuantityLimitsFeatureConfig === 'all' ||
                (customerInfo &&
                    ((defaultOrderQuantityLimitsFeatureConfig === 'b2b' && isB2BSite) ||
                        (defaultOrderQuantityLimitsFeatureConfig === 'b2c' && !isB2BSite)));

            return isOrderQuantityLimitsFeatureEnabledInSiteSettings ?? false;
        } else {
            const cartConfiguration = this.props.data.cartConfiguration?.result;
            return !(cartConfiguration?.IgnoreOrderSettings ?? false);
        }
    };

    /**
     * Save the address as Shipping and Billing address.
     * @param tokenizedPaymentCard - Tokenized payment card.
     */
    private readonly _saveShippingAndBillingAddress = async (tokenizedPaymentCard?: TokenizedPaymentCard): Promise<void> => {
        const checkoutState = this.props.data.checkout.result;

        // For Paypal, the address returned in TokenizedPaymentCard will be used as both shipping and billing address.
        if (checkoutState && tokenizedPaymentCard && tokenizedPaymentCard.Zip) {
            const address: Address = this._getAddressFromTokenizedPaymentCard(tokenizedPaymentCard);

            await checkoutState.updateBillingAddressFromExpressCheckout({ newBillingAddressFromExpressCheckout: address });
            await checkoutState.updateShippingAddressFromExpressCheckout({ newShippingAddressFromExpressCheckout: address });
        }
    };

    /**
     * Get address from tokenizedPaymentCard.
     * @param tokenizedPaymentCard -- The tokenizedPaymentCard from the payment.
     * @returns The address.
     */
    private readonly _getAddressFromTokenizedPaymentCard = (tokenizedPaymentCard: TokenizedPaymentCard): Address => {
        const address: Address = {
            TwoLetterISORegionName: tokenizedPaymentCard.Country,
            Name: tokenizedPaymentCard.NameOnCard,
            Street: tokenizedPaymentCard.Address1,
            StreetNumber: tokenizedPaymentCard.Address2,
            City: tokenizedPaymentCard.City,
            State: tokenizedPaymentCard.State,
            ZipCode: tokenizedPaymentCard.Zip,
            Phone: tokenizedPaymentCard.Phone
        };

        return address;
    };

    /**
     * Handle checkout errors.
     * @param error -- The error on checkout api failure.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private readonly handleCheckoutError = (error: any) => {
        const {
            resources: {
                genericErrorMessage,
                invalidCartVersionErrorMessage,
                missingRequiredCartTenderLinesErrorMessage,
                checkoutApiFailureErrorMessage,
                invoiceCheckoutApiFailureErrorMessage
            }
            // context: { actionContext }
        } = this.props;
        const checkoutResult = this.props.data.checkout.result;
        const apiError = error && error.message;
        if (error?.statusCode === 500) {
            const errorMessage = checkoutApiFailureErrorMessage;
            this.setState({
                errorMessage
            });
            this.props.telemetry.exception(error);
            this.setState({ isPlaceOrderLoading: false });
            return;
        }
        if (checkoutResult && checkoutResult.shouldEnableCheckoutErrorDisplayMessaging) {
            // const errorCode = error && error.name ? error.name : '';
            // apiError message will be used if errorcode resource/enums is unavailable
            // void setCheckoutErrors(errorCode, this.props.resources, !!checkoutResult.isExpressCheckoutApplied, actionContext, apiError);

            this.setState({
                errorMessage: genericErrorMessage,
                errorDetails: error?.data?.LocalizedMessage,
                errorCorrelationId: error?.data?.CorrelationId
            } as ICheckoutState);
            this.props.telemetry.exception(error);
        } else {
            let errorMessage = genericErrorMessage;
            this.setState({ isPlaceOrderClicked: false });

            if (error && error.name === 'Microsoft_Dynamics_Commerce_Runtime_InvalidCartVersion') {
                errorMessage = invalidCartVersionErrorMessage;
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- unknown error type.
            } else if (error && error.name === 'Microsoft_Dynamics_Commerce_Runtime_MissingRequiredCartTenderLines') {
                errorMessage = missingRequiredCartTenderLinesErrorMessage;
            } else if (error && error.name === 'Microsoft_Dynamics_Commerce_Runtime_ExclusiveCouponCannotBeAppliedWithOtherCoupons') {
                // If api error is not available show generic message
                errorMessage = apiError || genericErrorMessage;
            } else {
                if (error.status === ActionErrorStatus.Timeout || error.status === ActionErrorStatus.ServiceUnavailable) {
                    errorMessage = checkoutResult?.checkoutCart.hasInvoiceLine
                        ? invoiceCheckoutApiFailureErrorMessage
                        : checkoutApiFailureErrorMessage;
                }
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- unknown error type.
            if (
                this.props.data.checkout.result?.shouldEnableSinglePaymentAuthorizationCheckout &&
                error.data?.AdditionalContext &&
                !this.props.data.checkout.result?.isPaymentVerificationRedirection
            ) {
                return;
            }

            this.setState({
                errorMessage,
                errorDetails: error?.data?.LocalizedMessage,
                errorCorrelationId: error?.data?.CorrelationId
            } as ICheckoutState);
            this.props.telemetry.exception(error);
        }
    };

    private readonly handleUpdateCartResult = (result: ICartActionResult | ICheckoutActionResult): void => {
        if (result.status === AsyncResultStatusCode.FAILED) {
            if ('errorDetails' in result) {
                const message = result.errorDetails?.LocalizedMessage;
                this.props.telemetry.error(`Failed to update cart. Error message: ${message}`);
            } else {
                this.props.telemetry.error('Failed to update cart.');
            }
        }
    };

    private readonly calculateTotal = async (): Promise<void> => {
        const cartState = this.props.data.checkout.result!.checkoutCart;
        const checkoutAppliedState = this.props.data.checkoutAppliedState.result!;

        this.setState({
            /* errorMessage: '',
            errorDetails: '', */
            isCalculatingTotal: true,
            showSplitUpDeliveryModal: false
        });

        let result: ICartActionResult | ICheckoutActionResult;

        /* result = await cartState.updateCart({ newCartObject: rest as Cart });
        this.handleUpdateCartResult(result); */

        const updatedCart = await updateCartAsync({ callerContext: this.props.context.actionContext }, {
            ...cartState.cart,
            ...checkoutAppliedState.newCartObject
        });

        // Update internal email variables, skipping the API call
        result = await (await this.props.data.checkout).updateGuestCheckoutEmail({ newGuestCheckoutEmail: checkoutAppliedState.newCartObject.ReceiptEmail! }, true);
        this.handleUpdateCartResult(result);

        // Redo cart lines because of prices now being calculated
        const appliedCheckoutState = { ...checkoutAppliedState };
        appliedCheckoutState.newCartLinesObject = appliedCheckoutState.newCartLinesObject.map(x => {
            return {
                ...updatedCart.CartLines!.find(y => y.LineId === x.LineId),
                DeliveryMode: x.DeliveryMode,
                ShippingAddress: x.ShippingAddress,
                RequestedDeliveryDate: checkoutAppliedState.newCartObject.RequestedDeliveryDate
            };
        });
        result = await cartState.updateCartLines({ updatedCartLinesObject: appliedCheckoutState.newCartLinesObject });
        this.handleUpdateCartResult(result);

        // Check if we need to do a refreshCart here, but I think updateCartLines should be enough

        await this.addPaymentMax();

        this.setState({ hasCalculatedTotal: true, isCalculatingTotal: false });
    };

    private readonly getSplitUpDeliveryMessage = (): ICheckoutSplitDeliveryModalProps => {
        const { context, resources } = this.props;
        const { isCalculatingTotal, isPlaceOrderClicked, isPlaceOrderLoading } = this.state;
        const checkout = this.props.data.checkout?.result;
        const checkoutCart = checkout?.checkoutCart.cart;
        const Container: INodeProps = {
            className: 'ms-checkout__split-delivery'
        };

        // If there are any lines with missing delivery dates, assume the user hasn't filled in all information yet
        const anyMissingDeliveryDates = checkoutCart?.CartLines?.some(x => !x.RequestedDeliveryDate);
        const allLinesHaveSameDeliveryDateAsCart = checkoutCart?.CartLines?.every(x => x.RequestedDeliveryDate?.getTime() === checkoutCart.RequestedDeliveryDate?.getTime());

        if (isCalculatingTotal || isPlaceOrderClicked || isPlaceOrderLoading || anyMissingDeliveryDates || allLinesHaveSameDeliveryDateAsCart || !checkoutCart || !checkoutCart.CartLines) {
            // All lines have the same delivery date as the cart or there are no lines
            return { Container: null, header: null, button: null, message: null, modal: null };
        }

        const cartLines = [ ...checkoutCart.CartLines ];
        cartLines.sort((a, b) => {
            if (!a.RequestedDeliveryDate || !b.RequestedDeliveryDate) {
                return 0;
            }
            return b.RequestedDeliveryDate.getTime() - a.RequestedDeliveryDate.getTime();
        });

        const groupedLines: { [key: string]: CartLine[] } = {};
        for (const line of cartLines) {
            const key = line.RequestedDeliveryDate
                ? context.cultureFormatter.formatDate(line.RequestedDeliveryDate, {
                    day: 'numeric',
                    month: 'long'
                })
                : resources.splitDeliveryNoDeliveryDateErrorText;

            if (!groupedLines[key]) {
                groupedLines[key] = [];
            }
            groupedLines[key].push(line);
        }

        const header = (
            <h2 className='ms-checkout__split-delivery__heading'>
                {resources.splitDeliveryHeading}
            </h2>
        );
        const message = (
            <div className='ms-checkout__split-delivery__text'>
                {resources.splitDeliveryDescription}
            </div>
        );
        const button = (
            <Button
                className='ms-checkout__split-delivery__button'
                color='primary'
                onClick={async () => {
                    this.setState({ showSplitUpDeliveryModal: true, retrievingProductNames: true });

                    const productNames = this.state.productNames;
                    const productIds = cartLines.map(x => x.ProductId!).filter(Boolean) || [];

                    // Missing names
                    if (productIds.some(x => !productNames[x])) {
                        const products = await getByIdsAsync(
                            { callerContext: context.actionContext },
                            context.request.apiSettings.channelId,
                            productIds,
                            null,
                            context.request.apiSettings.catalogId);
                        const newProductNames = { ...productNames };

                        products.forEach(x => {
                            if (!x.RecordId || !x.Name) {
                                return;
                            }
                            newProductNames[x.RecordId] = x.Name;
                        });

                        this.setState({ productNames: newProductNames });
                    }

                    this.setState({ retrievingProductNames: false });
                }}
            >
                {resources.splitDeliveryShowButtonText}
            </Button>
        );

        const modal = (
            <Modal
                className='ms-checkout__split-delivery__modal'
                isOpen={this.state.showSplitUpDeliveryModal}
                toggle={() => this.setState({ showSplitUpDeliveryModal: !this.state.showSplitUpDeliveryModal })}
            >
                <ModalHeader closeAriaLabel='Close' toggle={() => this.setState({ showSplitUpDeliveryModal: !this.state.showSplitUpDeliveryModal })}>
                    {resources.splitDeliveryHeading}
                </ModalHeader>
                <ModalBody>
                    <table className='ms-checkout__split-delivery__table'>
                        <thead>
                            <tr>
                                <th align='left'>{resources.splitDeliveryColumnItemIdText}</th>
                                <th className='fill' align='left'>{resources.splitDeliveryColumnNameText}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.keys(groupedLines).map(group => {
                                return (
                                    <>
                                    <tr key={group} className='ms-checkout__split-delivery__table__group'>
                                        <td colSpan={3}>{resources.splitDeliveryGroupDateText}: {group}</td>
                                    </tr>
                                    {groupedLines[group].map(x => (
                                        <tr key={x.LineId} className='ms-checkout__split-delivery__table__row'>
                                            <td align='right'>{x.ItemId}</td>
                                            <td>{this.state.productNames[x.ProductId!]}</td>
                                        </tr>
                                    ))}
                                    </>
                                );
                            })}
                        </tbody>
                    </table>
                </ModalBody>
            </Modal>
        );

        return {
            Container,
            header,
            message,
            button,
            modal
        };
    };

    private readonly getReminderMessage = (): React.ReactNode => {
        const { resources } = this.props;
        const baseClassName = 'ms-checkout__reminder';

        if (this.state.hideReminderMessage) {
            return null;
        }

        return (
            <div className={`${baseClassName}__wrapper`}>
                <h3 className={`${baseClassName}__heading`}>
                    {resources.reminderHeading}
                </h3>
                <div
                    className={`${baseClassName}__text`}
                    dangerouslySetInnerHTML={{ __html: format(resources.reminderText, `<b>${resources.placeOrderText}</b>`) }}
                />
                <div className={`${baseClassName}__button-wrapper`}>
                    <button className={`${baseClassName}__button`} onClick={() => this.setState({ hideReminderMessage: true })}>
                        {resources.reminderButtonText}
                    </button>
                </div>
            </div>
        );
    };
}

export default Checkout;
